import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import type { UnavailableReason } from "_shared/_api/by-ids/by-ids-result";
import type { PolicyReplacementCreatedJSON } from "work/policy-replacements/_schemas/policy-replacement-schema";
import { POLICY_REPLACEMENT_SCOPE_DESCRIPTIONS } from "work/policy-replacements/policy-replacement-preview";

const UNAVAILABLE_EXPLANATIONS: Record<UnavailableReason, string> = {
  missing: "it no longer exists",
  unauthorized: "you may not revise it",
};

type PolicyReplacementCreatedOutcomeProps = {
  created: PolicyReplacementCreatedJSON;
  /** What to call each agreement, from the preview the client confirmed. */
  agreementNames: Readonly<Record<string, string>>;
};

/**
 * The selected agreements creating the replacement could not take, and why. The server checks every
 * agreement again when the replacement is created, and the preview is a snapshot, so an agreement can
 * change between the two. Every selected id is answered; none is dropped without a word.
 */
export default function PolicyReplacementCreatedOutcome(props: Readonly<PolicyReplacementCreatedOutcomeProps>) {
  const { created, agreementNames } = props;
  const nameOf = (workAgreementId: string) => agreementNames[workAgreementId] ?? "An agreement";
  const leftOut = [
    ...created.notReplaceable.map((entry) => ({
      id: entry.workAgreementId,
      why: POLICY_REPLACEMENT_SCOPE_DESCRIPTIONS[entry.scope].title.toLowerCase(),
    })),
    ...created.unavailable.map((entry) => ({ id: entry.id, why: UNAVAILABLE_EXPLANATIONS[entry.reason] })),
  ];
  if (leftOut.length === 0) return null;

  const nothingCreated = !created.id;
  return (
    <Alert severity={nothingCreated ? "error" : "warning"} role="alert">
      <AlertTitle>
        {nothingCreated
          ? "Nothing was replaced. No selected agreement could take the new policy."
          : `${leftOut.length} selected ${leftOut.length === 1 ? "agreement was" : "agreements were"} left out`}
      </AlertTitle>
      <ul>
        {leftOut.map((entry) => (
          <li key={entry.id}>
            {nameOf(entry.id)}: {entry.why}
          </li>
        ))}
      </ul>
    </Alert>
  );
}
